import { clamp, normalizeAngle } from "../../../core/util/MathUtil";
import { V } from "../../../core/Vector";
import { BaseMunition } from "../BaseMunition";
import { MunitionMovement } from "../BaseMunition";

const DEFAULT_SPEED = 60; // pixels per second
const DEFAULT_TURN_SPEED = 2.0; // radians per second
const DEFAULT_LIFETIME = 10.0; // seconds before self-destruct

export class TrackingMovement implements MunitionMovement {
  private speed: number;
  private turnSpeed: number;
  private lifetime: number;
  private timeAlive: number = 0;

  constructor(
    speed: number = DEFAULT_SPEED,
    turnSpeed: number = DEFAULT_TURN_SPEED,
    lifetime: number = DEFAULT_LIFETIME,
  ) {
    this.speed = speed;
    this.turnSpeed = turnSpeed;
    this.lifetime = lifetime;
  }

  update(munition: BaseMunition, dt: number): void {
    if (!munition.body || !munition.game) return;

    this.timeAlive += dt;

    // Self-destruct after lifetime expires
    if (this.timeAlive >= this.lifetime) {
      munition.explode();
      return;
    }

    // Find the turret to track
    const turrets = munition.game.entities.getTagged("turret");
    if (turrets.length === 0) return;
    const turret = turrets[0];

    const targetPos = V((turret as any).body?.position || [0, 0]);
    const currentPos = V(munition.body.position);

    // Calculate direction to target
    const directionToTarget = targetPos.sub(currentPos).normalize();

    // Get current velocity direction
    const currentVel = V(munition.body.velocity);
    if (currentVel.magnitude < 0.1) {
      // If not moving, start moving toward target
      munition.body.velocity = [
        directionToTarget.x * this.speed,
        directionToTarget.y * this.speed,
      ];
      return;
    }

    // Calculate current angle and target angle
    const currentAngle = currentVel.angle;
    const targetAngle = directionToTarget.angle;

    // Calculate the shortest angular distance
    let angleDiff = normalizeAngle(targetAngle - currentAngle);

    // Apply turn speed limit
    const turnAmount = clamp(
      angleDiff,
      -this.turnSpeed * dt,
      this.turnSpeed * dt,
    );
    const newAngle = currentAngle + turnAmount;

    // Set new velocity with constant speed
    const newVelocity = V(1, 0).rotate(newAngle).imul(this.speed);
    munition.body.velocity = newVelocity;
  }
}
